/**
 * The "tap here" cue a tour puts over the thing it is asking for.
 *
 * A step that waits for the seller to DO something — open a customer, confirm
 * an order — has no Next button, because pressing Next would teach the button
 * instead of the task. This is what stands in for it: a hand over the control,
 * nudging and pressing, with a ripple timed to the press.
 *
 * Shared by both tours (page help's `Coachmark` and the guided order tour), so
 * "the tour is waiting for you" looks the same wherever the seller meets it.
 * Motion lives in `styles.css` (`.coachmark-hand` / `.coachmark-pulse`), which
 * also drops the ripple and parks the hand under reduced motion.
 */
export function TapHand({
  /** The anchor being pointed at, in viewport pixels. */
  rect,
}: {
  rect: { top: number; left: number; width: number; height: number };
}) {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute block h-9 w-9"
      style={{
        // Over the middle of a short control, near the top of a tall one — a
        // hand parked halfway down a full-height list points at nothing.
        top: rect.top + Math.min(rect.height / 2, 40),
        left: rect.left + rect.width / 2 - 18,
      }}
    >
      {/* Ripple under the fingertip, timed to the hand's press beat: the hand
          says WHERE, the pulse says something happens there. */}
      <span className="coachmark-pulse" />
      <svg
        className="coachmark-hand relative"
        width="36"
        height="36"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M9 11V6.5a1.5 1.5 0 0 1 3 0V11m0-1.5a1.5 1.5 0 0 1 3 0V12m0-1a1.5 1.5 0 0 1 3 0v4.5a5.5 5.5 0 0 1-5.5 5.5h-1a5.5 5.5 0 0 1-5.5-5.5V13a1.5 1.5 0 0 1 3 0"
          fill="var(--surface)"
          stroke="var(--text-primary)"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}
